import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  browserPopupRedirectResolver,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, googleProvider } from '@/src/infrastructure/config/firebase';
import { syncUser, verifyAdmin } from '@/src/infrastructure/lib/api';
import { profileService, type UserProfile } from '@/src/modules/services/profile.service';
import { AuthContext, type AuthState } from './auth-context';
import { setUserId } from '../../infrastructure/lib/analytics';


export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bypassEnabled = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

  // Re-fetch the local profile. Safe to call anywhere; failures are
  // swallowed because a stale profile is preferable to evicting the user.
  const refreshProfile = useCallback(async () => {
    if (bypassEnabled) return;
    try {
      const fresh = await profileService.me();
      setProfile(fresh);
      setUserId(fresh.id);
    } catch {
      // ignore — keep the previous in-memory profile
    }
  }, [bypassEnabled]);

  // Capture the result of a redirect-based sign-in on first mount. If the
  // page just came back from the Google consent screen, this resolves with
  // the credential; otherwise it's a no-op.
  useEffect(() => {
    if (bypassEnabled) return;
    getRedirectResult(auth, browserPopupRedirectResolver).catch((e) => {
      setError(e instanceof Error ? e.message : 'Login failed');
    });
  }, [bypassEnabled]);

  useEffect(() => {
    if (bypassEnabled) {
      setUser({ uid: 'dev-user', email: 'dev@local', displayName: 'Dev User' } as any);
      setProfile({
        id: 'dev-user',
        email: 'dev@local',
        name: 'Dev User',
        role: 'SUPER_ADMIN',
        avatar: null,
      } as any);
      setUserId('dev-user');
      setIsAdmin(true);
      setLoading(false);
      return;
    }

    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (!next) {
        setProfile(null);
        setUserId(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      // Backend handshake on every sign-in:
      //   1. sync-user creates/refreshes the local DB row keyed off firebaseUid.
      //      Without this, the very first verifyAdmin() call 404s because no
      //      row exists yet for the Firebase identity.
      //   2. verifyAdmin then confirms the row's RBAC role is ADMIN.
      //   3. profileService.me() loads the canonical profile (name/avatar/
      //      updatedAt) so the navbar/avatars render from local DB state, not
      //      from the cached Firebase token (which doesn't refresh when the
      //      user edits their own profile).
      // verifyAdmin is the only source of truth for admin access — Firebase
      // only proves identity, never authorization.
      try {
        await syncUser({
          displayName: next.displayName ?? undefined,
          avatar: next.photoURL ?? undefined,
          provider: next.providerData[0]?.providerId,
        });
        await verifyAdmin();
        setIsAdmin(true);
        await refreshProfile();
      } catch {
        setIsAdmin(false);
        setProfile(null);
        setUserId(null);
      } finally {
        setLoading(false);
      }
    });
  }, [bypassEnabled, refreshProfile]);

  const loginWithGoogle = useCallback(async () => {
    setError(null);
    setLoading(true);
    if (bypassEnabled) {
      setUser({ uid: 'dev-user', email: 'dev@local', displayName: 'Dev User' } as any);
      setProfile({
        id: 'dev-user',
        email: 'dev@local',
        name: 'Dev User',
        role: 'SUPER_ADMIN',
        avatar: null,
      } as any);
      setUserId('dev-user');
      setIsAdmin(true);
      setLoading(false);
      return;
    }
    try {
      // Popup-first because `signInWithRedirect` stores OAuth state in
      // sessionStorage of <authDomain> (the project's *.firebaseapp.com
      // origin). Chrome's storage partitioning prevents the app origin
      // from reading it back, so `getRedirectResult` resolves null and
      // `onAuthStateChanged` never fires with a user.
      //
      // The COOP "popup.closed" warning is cosmetic — Firebase SDK handles
      // it and sign-in completes regardless. Redirect stays as a last
      // resort for environments where popups are genuinely blocked.
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      const code = (e as { code?: string })?.code ?? '';
      const popupBlocked =
        code === 'auth/popup-blocked' ||
        code === 'auth/operation-not-supported-in-this-environment';
      if (popupBlocked) {
        try {
          await signInWithRedirect(auth, googleProvider);
          return;
        } catch (re) {
          setError(re instanceof Error ? re.message : 'Login failed');
          setLoading(false);
          return;
        }
      }
      // Popup-closed-by-user / cancelled — treat as a quiet abort, no error.
      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request'
      ) {
        setLoading(false);
        return;
      }
      setError(e instanceof Error ? e.message : 'Login failed');
      setLoading(false);
    }
  }, [bypassEnabled]);

  const logout = useCallback(async () => {
    setUserId(null);
    if (bypassEnabled) {
      setUser(null);
      setProfile(null);
      setIsAdmin(false);
      return;
    }
    await signOut(auth);
    setIsAdmin(false);
    setProfile(null);
  }, [bypassEnabled]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      profile,
      isAdmin,
      loading,
      loginWithGoogle,
      logout,
      error,
      setProfile,
      refreshProfile,
    }),
    [user, profile, isAdmin, loading, loginWithGoogle, logout, error, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
